import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { useQuery } from '@apollo/client'
import { useConsumerCart } from './useConsumerCart'
import {
  CartErrorMessage,
  CartMutationResponse,
  CartProductError,
  ConsumerCart,
  DiscountError,
  DiscountWarning,
  DomesticAddress,
  GetCartFulfillmentOptionsResponse,
  InvalidTimeslotPrepTimeWarning,
  InvalidTimeslotWarning,
  MinimumValueWarning,
  OrderItem,
  OrderItemWithLimit,
  OriginalFulfillmentAvailableWarning
} from 'shop/types/cart'
import { useShop } from './useGlobalContext'
import { trackAddShippingInfoConsumerCart } from 'shop/components/Checkout/tracking/helpers'
import {
  CurrentLocationState,
  OrderFulfillmentType,
  PartnerStore
} from 'shop/components'
import {
  ASAP_ORDER,
  FulfillmentType,
  ORDER_AT_TABLE_FULFILLMENT,
  PREORDER_ORDER
} from 'shop/types'
import { findDefaultFulfillment } from 'shop/utils/common'
import {
  createDefaultCartParams,
  findErrorWarning,
  getAlcoholicItems,
  getCurrentCartId,
  isCartProductError,
  parseTimezoneISO
} from 'shop/utils'
import { useNavigate } from 'react-router-dom'
import {
  getCurrentDiscountWarningErrors,
  isCartErrorCode,
  isInvalidFulfillmentError,
  isInvalidTimeSlotPrepTimeWarning,
  isOriginalFulfillmentAvailableWarning,
  isTimeSlotWarning,
  trackTimeslotEvent
} from 'shop/components/Checkout/utils'
import { useQueryParams } from './useQueryParams'
import { TrackableEvent } from 'tracker/types'
import { useSessionStorage } from './useSessionStorage'
import { trackProductQuantityChange } from 'shop/components/ShopCart/utils'
import {
  consumerClientContext,
  QUERY_GET_CART_FULFILLMENT_OPTIONS
} from 'shop/client'
import {
  createCartFulfillmentDate,
  dateString,
  timeString
} from 'shop/components/Landing/utils'
import { format } from 'date-fns'

interface CheckoutState {
  giftWrapped: boolean
  setGiftWrapped: (value: boolean) => void
  giftWrapMessage: string | null
  setGiftWrapMessage: (message: string | null) => void
  trackAddShippingInfo: () => void
  updateCartGiftWrapping: (message: string | null) => void
  timeSlotValidation: InvalidTimeslotWarning | null
  dynamicPrepTimeSlotValidation: InvalidTimeslotPrepTimeWarning | null
  setDynamicPrepTimeSlotValidation: React.Dispatch<
    React.SetStateAction<InvalidTimeslotPrepTimeWarning | null>
  >
  productError: CartProductError | null
  discountErrorWarnings: (DiscountWarning | DiscountError)[]
  minimumOrderValueWarning: MinimumValueWarning | null
  rewardsRef: React.RefObject<HTMLDivElement>
  isPaying: boolean
  setIsPaying: React.Dispatch<React.SetStateAction<boolean>>
  isTippingOpen: boolean
  setIsTippingOpen: React.Dispatch<React.SetStateAction<boolean>>
  openTippingModal: () => void
  createDefaultCart: (
    currentPartnerStore: PartnerStore,
    queryParamFulfillmentType: FulfillmentType
  ) => Promise<ConsumerCart | void>
  hasAlcoholicProducts: boolean
  handleOrderItemQuantityChange: (
    orderItem: OrderItem,
    delta: number,
    proceedWithNewOrderItems?: boolean
  ) => Promise<CartMutationResponse | null>
  updateExceededLimitItems: (orderItems: OrderItemWithLimit[]) => Promise<void>
  handleFulfillmentTypeChange: (
    newFulfillmentType: FulfillmentType,
    proceedWithNewPrices?: boolean,
    deliveryAddress?: DomesticAddress
  ) => Promise<CartMutationResponse | null>
  toggleFulfillment: OrderFulfillmentType[] | null
  fulfillmentToggleDataLoading: boolean
  fetchFulfillmentToggleOptions: (cart: ConsumerCart | null) => Promise<void>
  isFulfillmentToggleTriggered: boolean
  setIsFulfillmentToggleTriggered: (triggered: boolean) => void
  invalidFulfillmentTypeError: CartErrorMessage.INVALID_FULFILLMENT_TYPE | null
}

const CheckoutContext = createContext<CheckoutState>({
  giftWrapped: false,
  setGiftWrapped: () => null,
  giftWrapMessage: null,
  setGiftWrapMessage: () => null,
  trackAddShippingInfo: () => null,
  updateCartGiftWrapping: () => null,
  timeSlotValidation: null,
  dynamicPrepTimeSlotValidation: null,
  setDynamicPrepTimeSlotValidation: () => null,
  productError: null,
  discountErrorWarnings: [],
  minimumOrderValueWarning: null,
  rewardsRef: { current: null },
  isPaying: false,
  setIsPaying: () => false,
  isTippingOpen: false,
  setIsTippingOpen: () => false,
  openTippingModal: () => null,
  createDefaultCart: () => new Promise<ConsumerCart | void>(() => {}),
  hasAlcoholicProducts: false,
  handleOrderItemQuantityChange: () =>
    new Promise<CartMutationResponse | null>(() => null),
  updateExceededLimitItems: () => Promise.resolve(),
  handleFulfillmentTypeChange: () => Promise.resolve(null),
  toggleFulfillment: null,
  fulfillmentToggleDataLoading: false,
  fetchFulfillmentToggleOptions: () => Promise.resolve(),
  isFulfillmentToggleTriggered: false,
  setIsFulfillmentToggleTriggered: () => {},
  invalidFulfillmentTypeError: null
})

export const CheckoutContextProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const {
    cart: consumerCart,
    initConsumerCart,
    updateConsumerCart,
    updateFulfillmentConsumerCart,
    warnings: consumerCartWarnings,
    errors: consumerCartErrors,
    updateOrderItemQuantityConsumerCart,
    removeOrderItemsConsumerCart
  } = useConsumerCart()
  const { partner, currentPartnerStore, config } = useShop()
  const navigate = useNavigate()
  const { deleteQueryParam } = useQueryParams()
  const { getSessionStorageItem: getSavedCurrentLocation } =
    useSessionStorage<CurrentLocationState>('saved-current-location')
  const storeName = consumerCart?.store?.name

  const [giftWrapped, setGiftWrapped] = useState<boolean>(false)
  const [giftWrapMessage, setGiftWrapMessage] = useState<string | null>(null)
  const [timeSlotValidation, setTimeSlotValidation] =
    useState<InvalidTimeslotWarning | null>(null)
  const [dynamicPrepTimeSlotValidation, setDynamicPrepTimeSlotValidation] =
    useState<InvalidTimeslotPrepTimeWarning | null>(null)
  const [discountErrorWarnings, setDiscountErrorWarnings] = useState<
    (DiscountWarning | DiscountError)[]
  >([])
  const [productError, setProductError] = useState<CartProductError | null>(
    null
  )
  const [minimumOrderValueWarning, setMinimumOrderValueWarning] =
    useState<MinimumValueWarning | null>(null)
  const [isPaying, setIsPaying] = useState<boolean>(false)
  const [isTippingOpen, setIsTippingOpen] = useState<boolean>(false)
  const [hasAlcoholicProducts, setHasAlcoholicProducts] =
    useState<boolean>(false)
  const [toggleFulfillment, setToggleFulfillment] = useState<
    OrderFulfillmentType[] | null
  >(null)
  // State to track loading of fulfillment toggle OFTs fetch
  const [fulfillmentToggleDataLoading, setFulfillmentToggleDataLoading] =
    useState<boolean>(true)
  // State to track whether the fulfillment toggle has been triggered to actually update fulfillment type
  const [isFulfillmentToggleTriggered, setIsFulfillmentToggleTriggered] =
    useState<boolean>(false)

  const [invalidFulfillmentTypeError, setInvalidFulfillmentTypeError] =
    useState<CartErrorMessage.INVALID_FULFILLMENT_TYPE | null>(null)

  // Ref used for intersection observer/scroll to rewards section on mobile when rewards are available
  const rewardsRef = useRef<HTMLDivElement>(null)

  const hasFetchedFulfillmentToggleOfts = useRef(false)
  const lastFetchedCartIdRef = useRef<string | null>(null)

  const { refetch: refetchFulfillmentToggleOptions } =
    useQuery<GetCartFulfillmentOptionsResponse>(
      QUERY_GET_CART_FULFILLMENT_OPTIONS,
      {
        ...consumerClientContext,
        fetchPolicy: 'network-only',
        skip: true
      }
    )

  const getGiftWrapState = () => {
    const { additionalItems } = consumerCart || {}
    const { giftWrapping } = additionalItems || {}
    const message = giftWrapping?.message || null
    return {
      giftWrapped: !!message,
      giftWrapMessage: message
    }
  }

  /** Gets & sets gift wrap state values on consumer cart load */
  useEffect(() => {
    const { giftWrapped, giftWrapMessage } = getGiftWrapState()
    setGiftWrapped(giftWrapped)
    setGiftWrapMessage(giftWrapMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCart?.id])

  /** Checks if there are alcoholic products in the cart & sets useState boolean for use in UI*/
  useEffect(() => {
    setHasAlcoholicProducts(
      !!(
        consumerCart?.orderItems &&
        getAlcoholicItems(consumerCart.orderItems).length
      )
    )
  }, [consumerCart?.orderItems])

  /** Sets latest toggleFulfillment OFTs for FulfillmentToggle functionality on the shop page */
  const fetchFulfillmentToggleOptions = async (cart: ConsumerCart | null) => {
    setFulfillmentToggleDataLoading(true)

    try {
      const cartId = getCurrentCartId(config.domain)
      // If we have a cart we can use the fulfillment from the cart
      const { window: cartWindow } = cart?.fulfillment || {}
      if (cartWindow) {
        const { data } = await refetchFulfillmentToggleOptions({
          fulfillmentDate: cartWindow.from.split('T')[0],
          fulfillmentTime: cartWindow.from.split('T')[1]
        })
        const orderFTypes = data?.cart?.store?.orderFulfillmentTypes || null
        orderFTypes && setToggleFulfillment(orderFTypes)
      }
      // Otherwise we have to use the defaults provided by the partner query
      else if (currentPartnerStore?.orderFulfillmentTypes && !cartId) {
        setToggleFulfillment(currentPartnerStore.orderFulfillmentTypes)
      }
    } catch (error) {
      console.error('Error fetching earliest cart fulfillment:', error)
    } finally {
      setFulfillmentToggleDataLoading(false)
    }
  }
  useEffect(() => {
    const cartId = getCurrentCartId(config.domain)

    // Skip if we've already fetched for this specific cartId
    if (
      hasFetchedFulfillmentToggleOfts.current &&
      lastFetchedCartIdRef.current === cartId
    ) {
      return
    }

    // If cartId exists, wait for cart's fulfillment window to load
    if (cartId && !consumerCart?.fulfillment?.window) {
      return
    }

    // Only fetch if we have either a cart with fulfillment window or partner store OFTs (when no cartId)
    if (
      consumerCart?.fulfillment?.window ||
      (currentPartnerStore?.orderFulfillmentTypes && !cartId)
    ) {
      fetchFulfillmentToggleOptions(consumerCart)
      lastFetchedCartIdRef.current = cartId
      hasFetchedFulfillmentToggleOfts.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    consumerCart?.fulfillment?.window,
    currentPartnerStore?.orderFulfillmentTypes
  ])

  /** Updates consumer cart on gift wrapping change & sets cart to show cost changes */
  const updateCartGiftWrapping = (message: string | null) => {
    // If gift wrapping is disabled, remove the message from the cart
    return updateConsumerCart({
      giftWrapMessage: message ? message : null
    })
  }

  /** Handles the opening of the Tipping Modal */
  const openTippingModal = () => {
    if (isPaying) return
    setIsTippingOpen(true)
  }

  /** Updates order item quantities to their limits when they exceed product limits */
  const updateExceededLimitItems = async (
    orderItems: OrderItemWithLimit[]
  ): Promise<void> => {
    const updatePromises = orderItems.map((item) => {
      const { quantity, limit, id } = item

      if (quantity === limit) {
        // Case A: quantity equals limit - don't update quantity
        return Promise.resolve()
      } else if (quantity > limit) {
        // Case B: quantity > limit - update quantity to the limit
        return updateOrderItemQuantityConsumerCart({
          id,
          quantity: limit
        }).catch((err) => console.error('UPDATE CART QUANTITY ERROR: ', err))
      }
      return Promise.resolve()
    })

    await Promise.all(updatePromises)
  }

  const trackAddShippingInfo = useCallback(() => {
    if (consumerCart && partner?.name && storeName) {
      trackAddShippingInfoConsumerCart(consumerCart, partner?.name, storeName)
    }
  }, [consumerCart, partner?.name, storeName])

  const handleTimeSlotUpdate = (
    warning: InvalidTimeslotWarning | OriginalFulfillmentAvailableWarning
  ) => {
    const handleUpdateSuccess = () => {
      trackTimeslotEvent(TrackableEvent.UpdateTimeslot)
      setTimeSlotValidation(null)
    }
    const handleUpdateError = () => {
      console.log('Error updating timeslot')
    }

    // Handles InvalidTimeslotWarning
    if ('nextAvailableTimeslot' in warning) {
      const { nextAvailableTimeslot } = warning
      if (!nextAvailableTimeslot) return

      updateFulfillmentConsumerCart({
        fulfillmentTime: nextAvailableTimeslot.value
      })
        .then(handleUpdateSuccess)
        .catch(handleUpdateError)
    } else {
      // Handles OriginalFulfillmentAvailableWarning
      const { fulfillmentDate, fulfillmentTime } = warning
      updateFulfillmentConsumerCart({
        fulfillmentDate,
        fulfillmentTime
      })
        .then(handleUpdateSuccess)
        .catch(handleUpdateError)
    }
  }

  /** Handles consumer cart timeslot and MOV warnings */
  useEffect(() => {
    if (!consumerCartWarnings.length) {
      if (!!timeSlotValidation) setTimeSlotValidation(null)
      if (!!dynamicPrepTimeSlotValidation)
        setDynamicPrepTimeSlotValidation(null)
      if (!!minimumOrderValueWarning) setMinimumOrderValueWarning(null)
      return
    }

    const movWarning = findErrorWarning(
      consumerCartWarnings,
      isCartErrorCode
    ) as MinimumValueWarning

    const timeslotWarning = findErrorWarning(
      consumerCartWarnings,
      isTimeSlotWarning
    ) as InvalidTimeslotWarning

    const dynamicPrepTimeSlotWarning = findErrorWarning(
      consumerCartWarnings,
      isInvalidTimeSlotPrepTimeWarning
    ) as InvalidTimeslotPrepTimeWarning

    const originalFulfillmentAvailableWarning = findErrorWarning(
      consumerCartWarnings,
      isOriginalFulfillmentAvailableWarning
    ) as OriginalFulfillmentAvailableWarning

    setDynamicPrepTimeSlotValidation(dynamicPrepTimeSlotWarning || null)

    setMinimumOrderValueWarning(movWarning || null)

    if (originalFulfillmentAvailableWarning) {
      // If the original fulfillment is available, update silently for user without showing modal
      handleTimeSlotUpdate(originalFulfillmentAvailableWarning)
      return
    }

    if (timeslotWarning) {
      const { nextAvailableTimeslot, isStoreOpen } = timeslotWarning
      // If there is a next available timeslot, update silently for user without showing modal
      if (nextAvailableTimeslot?.value && isStoreOpen) {
        handleTimeSlotUpdate(timeslotWarning)
        return
      }

      setTimeSlotValidation(timeslotWarning)
    } else setTimeSlotValidation(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCartWarnings])

  /** Handles cart product errors */
  useEffect(() => {
    if (!consumerCartErrors.length) {
      setProductError(null)
      setInvalidFulfillmentTypeError(null)
      return
    }

    const currentProductError = findErrorWarning(
      consumerCartErrors,
      isCartProductError
    ) as CartProductError

    const invalidFulfillmentTypeError =
      (findErrorWarning(consumerCartErrors, isInvalidFulfillmentError)
        ?.message as CartErrorMessage.INVALID_FULFILLMENT_TYPE) ?? null

    setInvalidFulfillmentTypeError(invalidFulfillmentTypeError)

    setProductError(currentProductError || null)
  }, [consumerCartErrors])

  /** Handles consumer cart discount warnings/errors */
  useEffect(() => {
    if (!consumerCartWarnings.length && !consumerCartErrors.length) {
      // reset all validations if there are no consumerCartWarnings
      if (!!discountErrorWarnings.length) setDiscountErrorWarnings([])
      return
    }

    const currentDiscountErrorWarnings = getCurrentDiscountWarningErrors(
      consumerCartErrors,
      consumerCartWarnings
    )

    if (currentDiscountErrorWarnings.length) {
      setDiscountErrorWarnings(currentDiscountErrorWarnings)
    } else setDiscountErrorWarnings([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerCartErrors, consumerCartWarnings])

  const createDefaultCart = async (
    currentPartnerStore: PartnerStore,
    queryParamFulfillmentType: FulfillmentType
  ) => {
    if (!currentPartnerStore) return

    // Find first orderFulfillment for current store which is ACCEPTING_ORDERS
    const defaultFulfillmentInfo =
      findDefaultFulfillment(
        currentPartnerStore.orderFulfillmentTypes,
        queryParamFulfillmentType
      ) ?? null

    // If there are no fulfillments ACCEPTING_ORDERS return and no cart is created
    if (!defaultFulfillmentInfo) {
      return
    }

    const fulfillmentType: FulfillmentType =
      queryParamFulfillmentType || defaultFulfillmentInfo.fulfillmentType

    // Table orders should redirect to landing page so return early,
    if (fulfillmentType === ORDER_AT_TABLE_FULFILLMENT) {
      return navigate('/')
    }

    const slerpCartParams = createDefaultCartParams(
      currentPartnerStore?.id,
      fulfillmentType,
      defaultFulfillmentInfo,
      getSavedCurrentLocation()
    )
    if (!slerpCartParams) {
      return
    }

    // Create cart using consumer API
    return initConsumerCart({ variables: slerpCartParams })
      .then((res) => {
        if (!res) {
          return
        }

        const { cart } = res || {}

        if (!cart) {
          return navigate('/')
        }
        // Delete query params to prevent them from persisting & affecting the isCartMatch value
        deleteQueryParam(['fulfillment_type', 'date', 'time'])

        return cart
      })
      .catch((err) => {
        console.error(err)
      })
  }

  const handleRemoveCartItem = useCallback(
    async (orderItem: OrderItem): Promise<CartMutationResponse | null> => {
      const { id, product, total, quantity } = orderItem
      const res = await removeOrderItemsConsumerCart({ ids: [id] })
      const { cart: updatedCart } = res || {}
      if (updatedCart) {
        // When removing an item entirely, delta equals the original item quantity as we're removing all units of that item from the cart
        trackProductQuantityChange(
          TrackableEvent.ProductRemoved,
          { product, total, quantity, delta: quantity },
          storeName,
          partner?.name
        )
      }
      return res
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner?.name, storeName]
  )

  const handleOrderItemQuantityChange = async (
    orderItem: OrderItem,
    delta: number,
    proceedWithNewOrderItems?: boolean
  ): Promise<CartMutationResponse | null> => {
    const { id, quantity, product, total } = orderItem
    if (quantity + delta <= 0) {
      const res = await handleRemoveCartItem(orderItem)
      return res
    }

    const res = await updateOrderItemQuantityConsumerCart({
      id,
      quantity: quantity + delta,
      ...(!!proceedWithNewOrderItems && { proceedWithNewOrderItems })
    })

    const { cart: updatedCart } = res || {}

    if (updatedCart) {
      trackProductQuantityChange(
        delta > 0 ? TrackableEvent.ProductAdded : TrackableEvent.ProductRemoved,
        { product, total, quantity, delta },
        storeName,
        partner?.name
      )
    }
    return res
  }

  const handleFulfillmentTypeChange = async (
    fulfillmentType: FulfillmentType,
    proceedWithNewPrices?: boolean,
    deliveryAddress?: DomesticAddress
  ) => {
    if (!toggleFulfillment?.length) return null

    // Finds the available OFT that matches the fulfillmentType provided
    const orderFulfillmentType = findDefaultFulfillment(
      toggleFulfillment,
      fulfillmentType
    )

    const { earliestTimeslot, orderType } = orderFulfillmentType || {}

    if (!earliestTimeslot) return null

    const newWindowFromDate = earliestTimeslot
      ? parseTimezoneISO(earliestTimeslot.to)
      : null

    const isAsap = orderType === ASAP_ORDER

    const cartfulfillmentTime =
      orderType === ASAP_ORDER
        ? null
        : format(parseTimezoneISO(earliestTimeslot.from), 'HH:mm')

    const cartFulfillmentDate = createCartFulfillmentDate(
      isAsap ? null : newWindowFromDate,
      orderType === PREORDER_ORDER
    )

    const newTime = timeString(cartfulfillmentTime) || null

    // Set the flag to indicate fulfillment toggle was triggered
    setIsFulfillmentToggleTriggered(true)

    const updatedCartRes = await updateFulfillmentConsumerCart({
      fulfillmentDate: dateString(cartFulfillmentDate),
      fulfillmentType: fulfillmentType,
      fulfillmentTime: newTime,
      ...(deliveryAddress && {
        deliveryAddress: { ...deliveryAddress, country: 'United Kingdom' }
      }),
      ...(proceedWithNewPrices && { proceedWithNewPrices })
    })

    return updatedCartRes
  }

  return (
    <CheckoutContext.Provider
      value={{
        giftWrapped,
        setGiftWrapped,
        giftWrapMessage,
        setGiftWrapMessage,
        trackAddShippingInfo,
        updateCartGiftWrapping,
        timeSlotValidation,
        dynamicPrepTimeSlotValidation,
        setDynamicPrepTimeSlotValidation,
        productError,
        discountErrorWarnings,
        minimumOrderValueWarning,
        rewardsRef,
        isPaying,
        setIsPaying,
        isTippingOpen,
        setIsTippingOpen,
        openTippingModal,
        createDefaultCart,
        hasAlcoholicProducts,
        handleOrderItemQuantityChange,
        updateExceededLimitItems,
        handleFulfillmentTypeChange,
        toggleFulfillment,
        fulfillmentToggleDataLoading,
        fetchFulfillmentToggleOptions,
        isFulfillmentToggleTriggered,
        setIsFulfillmentToggleTriggered,
        invalidFulfillmentTypeError
      }}
    >
      {children}
    </CheckoutContext.Provider>
  )
}

export const useCheckout = () => useContext(CheckoutContext)
